import { useState, useEffect } from 'react';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, TextField, InputAdornment, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';
import * as XLSX from 'xlsx-js-style';

const STATUS_OPTIONS = [
  { value: 0, label: 'SELECT' },
  { value: 1, label: 'New' },
  { value: 2, label: 'InProgress' },
  { value: 3, label: 'Pending' },
  { value: 4, label: 'Completed' },
  { value: 5, label: 'Delivered' },
];

const STATUS_COLOR = {
  New: '#1565c0',
  InProgress: '#f57c00',
  Pending: '#6a1b9a',
  Completed: '#388e3c',
  Delivered: '#7b1fa2',
};

function WorkOrderReport() {
  useSessionValidation();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { isAuthenticated, companyId, logout, token, companyName } = useAuth();

  const today = new Date().toISOString().split('T')[0];
  const now = new Date();
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [statusId, setStatusId] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) logout();
  }, [isAuthenticated, companyId]); // eslint-disable-line

  const fetchTableData = async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const tmpType = 'WorkOrderDetails';
      const branch = statusId || 0;
      const response = await fetchData(
        'ReportApi',
        `/${tmpType}/${companyId}/${branch}/${fromDate}/${toDate}`,
        token
      );
      if (response && response.responseCode === '1111' && response.response_data) {
        const rows = Array.isArray(response.response_data)
          ? response.response_data
          : [response.response_data];
        setData(rows);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching WorkOrder report:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchTableData();
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    const now = new Date();
    const generatedTime = now.toLocaleString('en-IN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
    const statusLabel = STATUS_OPTIONS.find(s => s.value === statusId)?.label || 'All';

    const COLS = 13; // total columns (0-12)

    // Build full AOA: info rows + blank + header + data
    const aoa = [
      [companyName || 'Work Order Report', ...Array(COLS - 1).fill('')],           // row 0
      [`Work Order Details Report`, ...Array(COLS - 1).fill('')],                  // row 1
      [`Date Range : ${fromDate}  To : ${toDate}`, ...Array(COLS - 1).fill('')],  // row 2
      [`Status Filter : ${statusLabel}`, ...Array(COLS - 1).fill('')],             // row 3
      [`Generated : ${generatedTime}`, ...Array(COLS - 1).fill('')],               // row 4
      Array(COLS).fill(''),                                                         // row 5 blank
      ['Order Number','Order Date','Estimate Date','Client Name','Contact No',      // row 6 header
       'Address','Amount','Other Amount','Total Amount','Advance Amount',
       'Balance Amount','Status','Remarks'],
      ...filteredData.map(item => [                                                 // row 7+
        item.OrderNumber || '',        
        item.EstimateDate ? item.EstimateDate.split('T')[0] : '',
        item.OrderDate ? item.OrderDate.split('T')[0] : '',
        item.ClientName || '',
        item.contactno || '',
        item.Address || '',
        parseFloat(item.Amount || 0),
        parseFloat(item.OtherAmount || 0),
        parseFloat(item.TotalAmount || 0),
        parseFloat(item.AdvanceAmount || 0),
        parseFloat(item.BalanceAmount || 0),
        item.Statusname || '',
        item.remarks || '',
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Column widths
    ws['!cols'] = [
      { wch: 13 }, { wch: 12 }, { wch: 14 }, { wch: 22 }, { wch: 14 },
      { wch: 28 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
      { wch: 14 }, { wch: 12 }, { wch: 30 },
    ];

    // Row heights
    ws['!rows'] = [
      { hpt: 22 }, // company name
      { hpt: 18 }, // report title
      { hpt: 16 }, // date range
      { hpt: 16 }, // status
      { hpt: 16 }, // generated
      { hpt: 6  }, // blank
      { hpt: 20 }, // header
    ];

    // Merges for info rows
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: COLS - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: COLS - 1 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: COLS - 1 } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: COLS - 1 } },
    ];

    // Style helpers
    const headerStyle = {
      font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '5c3d1e' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: false },
      border: {
        top:    { style: 'medium', color: { rgb: '3b2410' } },
        bottom: { style: 'medium', color: { rgb: '3b2410' } },
        left:   { style: 'thin',   color: { rgb: '3b2410' } },
        right:  { style: 'thin',   color: { rgb: '3b2410' } },
      },
    };

    const dataStyle = (even, rightAlign) => ({
      font: { sz: 10 },
      fill: { patternType: 'solid', fgColor: { rgb: even ? 'FFFFFF' : 'FDF6EC' } },
      alignment: { horizontal: rightAlign ? 'right' : 'left', vertical: 'center' },
      border: {
        top:    { style: 'thin', color: { rgb: 'E0E0E0' } },
        bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
        left:   { style: 'thin', color: { rgb: 'E0E0E0' } },
        right:  { style: 'thin', color: { rgb: 'E0E0E0' } },
      },
    });

    // Apply info row styles
    ws['A1'].s = { font: { bold: true, sz: 16, color: { rgb: '5c3d1e' } }, alignment: { horizontal: 'center', vertical: 'center' }, fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } } };
    ws['A2'].s = { font: { bold: true, sz: 13, color: { rgb: '2d3748' } }, alignment: { horizontal: 'center', vertical: 'center' }, fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } } };
    ws['A3'].s = { font: { bold: false, sz: 11, color: { rgb: '444444' } }, alignment: { horizontal: 'center', vertical: 'center' }, fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } } };
    ws['A4'].s = { font: { bold: false, sz: 11, color: { rgb: '444444' } }, alignment: { horizontal: 'center', vertical: 'center' }, fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } } };
    ws['A5'].s = { font: { bold: false, sz: 11, color: { rgb: '444444' } }, alignment: { horizontal: 'center', vertical: 'center' }, fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } } };

    // Apply header row styles (row index 6)
    for (let c = 0; c < COLS; c++) {
      const addr = XLSX.utils.encode_cell({ r: 6, c });
      if (ws[addr]) ws[addr].s = headerStyle;
    }

    // Apply data row styles (row index 7+)
    const numericCols = new Set([6, 7, 8, 9, 10]); // Amount cols
    for (let r = 7; r < aoa.length; r++) {
      const even = (r - 7) % 2 === 0;
      for (let c = 0; c < COLS; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = dataStyle(even, numericCols.has(c));
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Work Order Details');
    XLSX.writeFile(wb, `WorkOrderReport_${fromDate}_${toDate}.xlsx`, { bookSST: false, cellStyles: true });
  };

  const filteredData = data.filter(item =>
    item.ClientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.contactno?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.OrderNumber || '').includes(searchTerm) ||
    item.remarks?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const headerCell = (label, align = 'left') => ({
    color: 'white', fontWeight: '600', fontSize: '11px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    padding: '4px 12px', textAlign: align,
    whiteSpace: 'nowrap',
    backgroundColor: '#5c3d1e',
  });

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Work Order Report..." />}

        <div style={{
          margin: '0 auto', background: 'white',
          borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          padding: '12px', color: 'black', fontFamily: 'Times New Roman',
          overflow: 'hidden',
        }}>
          {/* Title */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0',
          }}>
            <h4 style={{
              fontSize: '17px', fontWeight: '700', margin: 0,
              letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase',
            }}>
              <span style={{ color: 'black' }}>📋 Work Order Details</span>
            </h4>
          </div>

          {/* Filters Row */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>From Date</label>
            <input
              type="date" value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              style={{
                border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px',
                fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333',
                outline: 'none', cursor: 'pointer',
              }}
            />
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>To date</label>
            <input
              type="date" value={toDate}
              onChange={e => setToDate(e.target.value)}
              style={{
                border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px',
                fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333',
                outline: 'none', cursor: 'pointer',
              }}
            />
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>Status</label>
            <select
              value={statusId}
              onChange={e => setStatusId(Number(e.target.value))}
              style={{
                border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px',
                fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333',
                outline: 'none', cursor: 'pointer', minWidth: '130px',
              }}
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <Button
              variant="contained"
              onClick={handleRefresh}
              style={{
                background: 'linear-gradient(135deg, #388e3c 0%, #43a047 100%)',
                color: 'white', padding: '4px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', border: 'none',
                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(56,142,60,0.4)',
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              onClick={handleExportExcel}
              disabled={filteredData.length === 0}
              style={{
                background: filteredData.length === 0 ? '#ccc' : 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                color: 'white', padding: '4px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', border: 'none',
                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                boxShadow: filteredData.length === 0 ? 'none' : '0 2px 8px rgba(21,101,192,0.4)',
              }}
            >
              📥 Excel
            </Button>

            {/* Search - pushed to right */}
            <div style={{ flex: 1 }} />
            <TextField
              size="small" placeholder="Search..." value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#c9a84c', fontSize: '18px' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: '180px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px', backgroundColor: '#fdf6ec',
                  fontSize: '13px', height: '29px',
                  '&:hover fieldset': { borderColor: '#c9a84c' },
                  '&.Mui-focused fieldset': { borderColor: '#c9a84c' },
                },
              }}
            />
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <style>{`
              .wo-scroll::-webkit-scrollbar { height: 10px; width: 8px; }
              .wo-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
              .wo-scroll::-webkit-scrollbar-thumb { background: #c9a84c; border-radius: 4px; }
              .wo-scroll::-webkit-scrollbar-thumb:hover { background: #5c3d1e; }
              .wo-scroll { scrollbar-width: thin; scrollbar-color: #c9a84c #f1f1f1; }
            `}</style>
            <div className="wo-scroll" style={{ overflowX: 'scroll', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0, overflow: 'visible' }}>
              <Table stickyHeader sx={{ minWidth: 1200 }} aria-label="work order report table">
                <TableHead>
                  <TableRow style={{ height: '27px' }}>
                    <TableCell style={headerCell('Order Number')}>Order Number</TableCell>                    
                    <TableCell style={headerCell('Estimate Date')}>Estimate Date</TableCell>
                    <TableCell style={headerCell('Order Date')}>Order Date</TableCell>
                    <TableCell style={headerCell('Client Name')}>Client Name</TableCell>
                    <TableCell style={headerCell('Contact No')}>Contact No</TableCell>
                    <TableCell style={headerCell('Address')}>Address</TableCell>
                    <TableCell style={headerCell('Amount', 'right')}>Amount</TableCell>
                    <TableCell style={headerCell('Other Amt', 'right')}>Other Amt</TableCell>
                    <TableCell style={headerCell('Total', 'right')}>Total</TableCell>
                    <TableCell style={headerCell('Advance', 'right')}>Advance</TableCell>
                    <TableCell style={headerCell('Balance', 'right')}>Balance</TableCell>
                    <TableCell style={headerCell('Status', 'center')}>Status</TableCell>
                    <TableCell style={{ ...headerCell('Remarks'), minWidth: '160px' }}>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>
                        {loading ? 'Loading...' : 'No records found. Select filters and click Refresh.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((item, index) => (
                      <TableRow
                        key={item.EstimateId || index}
                        style={{ background: index % 2 === 0 ? '#ffffff' : '#fdf6ec', borderBottom: '1px solid #f0f0f0' }}
                      >
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {item.OrderNumber}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', whiteSpace: 'nowrap' }}>
                          {item.OrderDate ? item.OrderDate.split('T')[0] : '—'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', whiteSpace: 'nowrap' }}>
                          {item.EstimateDate ? item.EstimateDate.split('T')[0] : '—'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {item.ClientName || '—'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', whiteSpace: 'nowrap' }}>
                          {item.contactno || '—'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', whiteSpace: 'nowrap' }}>
                          {item.Address || '—'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {parseFloat(item.Amount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {parseFloat(item.OtherAmount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'right', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {parseFloat(item.TotalAmount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {parseFloat(item.AdvanceAmount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'right', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          {parseFloat(item.BalanceAmount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {item.Statusname ? (
                            <Chip
                              label={item.Statusname}
                              size="small"
                              style={{
                                fontSize: '10px', height: '18px',
                                backgroundColor: STATUS_COLOR[item.Statusname] || '#757575',
                                color: 'white', fontWeight: '600',
                              }}
                            />
                          ) : '—'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', whiteSpace: 'nowrap', maxWidth: '160px', overflow: 'auto', display: 'table-cell' }}>
                          <div style={{ maxWidth: '160px', overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '2px' }}>
                            {item.remarks || '—'}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </div>
          </div>

          <Pagination
            currentPage={currentPage} totalPages={totalPages}
            totalItems={totalItems} itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={n => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </div>
    </Fade>
  );
}

export default WorkOrderReport;
